import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Instagram, Facebook, Mail, Twitter, Youtube, Linkedin, Phone, ChevronDown, ShieldCheck, Accessibility, Download } from 'lucide-react';
import useSiteConfig from '../hooks/useSiteConfig';
import { publicDpoApi } from '../services/api';
import { resolveBrandLogo, resolveBackendImage } from '../utils/brandLogo';
import BackendImage from './BackendImage';
import { usePWAInstall } from '../hooks/usePWAInstall';
import useMunicipioBasePath from '../hooks/useMunicipioBasePath';

// Mobile-only collapsible section. On md+ the header is rendered statically
// (no toggle button) and the children are always visible.
function FooterSection({ title, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 md:border-0">
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden w-full flex items-center justify-between py-3 text-left"
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <h4 className="font-semibold text-white text-sm">{title}</h4>
        <ChevronDown
          className={`w-4 h-4 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Desktop static heading */}
      <h4 className="hidden md:block font-semibold text-white mb-4">{title}</h4>
      <div className={`${open ? 'block pb-3' : 'hidden'} md:block`}>{children}</div>
    </div>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { config, loading: configLoading } = useSiteConfig();
  const [dpoInfo, setDpoInfo] = useState(null);
  const { isInstalled } = usePWAInstall();
  const { basePath } = useMunicipioBasePath();

  useEffect(() => {
    publicDpoApi.info()
      .then((d) => setDpoInfo(d?.exibir && d?.nome ? d : null))
      .catch(() => {});
  }, []);

  // Prioridade: logo_footer customizado → logo_principal customizado → null
  // (sem fallback SVG embutido; renderiza placeholder vazio enquanto carrega).
  // `resolveBackendImage` normaliza o host quando a URL absoluta aponta para
  // o backend (preview → produção), evitando broken images em domínios custom.
  const logoUrl = resolveBackendImage(config.logo_footer)
    || resolveBrandLogo(config);

  // Redes sociais disponíveis
  const socialLinks = [
    { url: config.instagram_url, icon: Instagram, label: 'Instagram', color: 'hover:bg-[#FFA52A]' },
    { url: config.facebook_url, icon: Facebook, label: 'Facebook', color: 'hover:bg-[#FFA52A]' },
    { url: config.twitter_url, icon: Twitter, label: 'Twitter', color: 'hover:bg-[#FFA52A]' },
    { url: config.youtube_url, icon: Youtube, label: 'YouTube', color: 'hover:bg-[#FFA52A]' },
    { url: config.linkedin_url, icon: Linkedin, label: 'LinkedIn', color: 'hover:bg-[#FFA52A]' },
  ].filter(link => link.url);

  return (
    <footer className="bg-[#56007F] text-white" data-testid="footer" role="contentinfo">
      {/* Main Footer */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1 md:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-1 text-center md:text-left mb-6 md:mb-0">
            {configLoading || !logoUrl ? (
              <div className="h-12 w-40 mb-4 mx-auto md:mx-0" aria-hidden="true" />
            ) : (
              <BackendImage
                src={logoUrl}
                alt="Empreende Comunidade"
                className="h-12 w-auto object-contain mb-4 mx-auto md:mx-0"
                eager
                fallback={<div className="h-12 w-40 mb-4 mx-auto md:mx-0" aria-hidden="true" />}
              />
            )}
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              {config.descricao_footer || 'Conectando você aos melhores negócios da sua região. Fortaleça a economia local!'}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-4 justify-center md:justify-start">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 bg-white/10 rounded-full flex items-center justify-center ${social.color} transition-colors`}
                      aria-label={social.label}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Institucional Column */}
          <FooterSection title="Institucional" testId="footer-institucional">
            <ul className="space-y-3">
              <li>
                <Link to={`${basePath}/sobre`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Sobre o Projeto
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/ajuda`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Ajuda
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/faq`} className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-faq-link">
                  Perguntas Frequentes
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/produtos-da-roca`} className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-produtos-roca-link">
                  Produtos da Roça
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/economia-criativa`} className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-economia-criativa-link">
                  Economia Criativa
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/noticias`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Notícias
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/fale-conosco`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Fale Conosco
                </Link>
              </li>
              {!isInstalled && (
                <li>
                  <Link
                    to={`${basePath}/instalar`}
                    className="text-white/70 hover:text-white transition-colors text-sm flex items-center gap-1.5"
                    data-testid="footer-install-app"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Instalar app
                  </Link>
                </li>
              )}
            </ul>
          </FooterSection>

          {/* Legal Column */}
          <FooterSection title="Legal" testId="footer-legal">
            <ul className="space-y-3">
              <li>
                <Link to={`${basePath}/termos-de-uso`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Termos de Uso
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/politica-de-privacidade`} className="text-white/70 hover:text-white transition-colors text-sm">
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link
                  to={`${basePath}/acessibilidade`}
                  className="text-white/70 hover:text-white transition-colors text-sm"
                  data-testid="footer-acessibilidade-link"
                >
                  Acessibilidade
                </Link>
              </li>
              <li>
                <Link
                  to={`${basePath}/privacidade/solicitacao`}
                  className="text-white/70 hover:text-white transition-colors text-sm"
                  data-testid="footer-solicitacao-link"
                >
                  Exercer direitos LGPD (Art. 18)
                </Link>
              </li>
              {/* Encarregado de Proteção de Dados (DPO) — Art. 41 §1º LGPD */}
              <li
                className="pt-2 mt-2 border-t border-white/10 text-white/70 text-xs leading-relaxed"
                data-testid="footer-dpo"
              >
                <div className="flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white/90 mb-0.5">Encarregado de Dados (DPO)</p>
                    {dpoInfo ? (
                      <>
                        <p>{dpoInfo.nome}</p>
                        <a href={`mailto:${dpoInfo.email}`} className="underline hover:text-white break-all">
                          {dpoInfo.email}
                        </a>
                      </>
                    ) : (
                      <a
                        href="mailto:dpo@empreendecomunidade.com.br"
                        className="underline hover:text-white break-all"
                      >
                        dpo@empreendecomunidade.com.br
                      </a>
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </FooterSection>

          {/* Contact Column */}
          <FooterSection title="Contato" testId="footer-contato">
            <ul className="space-y-3">
              {config.email_contato && (
                <li className="flex items-start gap-2 text-white/70 text-sm min-w-0">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <a
                    href={`mailto:${config.email_contato}`}
                    className="hover:text-white transition-colors break-all min-w-0"
                  >
                    {config.email_contato}
                  </a>
                </li>
              )}
              {config.telefone_contato && (
                <li className="flex items-start gap-2 text-white/70 text-sm min-w-0">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <a
                    href={`tel:${config.telefone_contato}`}
                    className="hover:text-white transition-colors break-all min-w-0"
                  >
                    {config.telefone_contato}
                  </a>
                </li>
              )}
              {config.endereco && (
                <li className="flex items-start gap-2 text-white/70 text-sm min-w-0">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="break-words min-w-0">{config.endereco}</span>
                </li>
              )}
            </ul>
          </FooterSection>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm text-center md:text-left">
              © {currentYear} Empreende Comunidade.
              {/* Quebra a linha apenas no mobile */}
              <br className="md:hidden" />
              <span className="md:ml-1">Todos os direitos reservados.</span>
            </p>
            {/* Mobile: WCAG em cima, "Feito com ♥" abaixo, centralizado.
                Desktop: lado a lado, como antes. */}
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
              <Link
                to={`${basePath}/acessibilidade`}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FFA52A] text-[#3d005a] rounded-full text-xs font-bold hover:bg-[#ffb84c] transition-colors"
                data-testid="footer-wcag-badge"
                aria-label="Conformidade WCAG 2.1 AA — saiba mais sobre acessibilidade"
                title="Conformidade WCAG 2.1 AA — clique para ver os recursos de acessibilidade"
              >
                <Accessibility className="w-3.5 h-3.5" aria-hidden="true" />
                WCAG 2.1 AA
              </Link>
              <p className="text-white/60 text-sm flex items-center gap-1">
                Feito com <Heart className="w-4 h-4 text-red-400 fill-red-400" aria-hidden="true" /> para a comunidade
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
